import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { parse, format } from "date-fns";
import { toast } from "@/hooks/use-toast";

interface CertificateDownloadProps {
  fullName: string;
  certId: string;
  date: string;
  // When provided, downloading also emails the Certificate + Letter of Good Standing
  // to the student (per spec). Dynamic import avoids a circular dependency.
  applicationId?: string;
}

// Kept for preview iframes in Certificates.tsx and InlineCertificateViewer.tsx
export const generateCertificateHTML = (fullName: string, certId: string, date: string) => {
  return `<!DOCTYPE html>
<html>
<head>
<style>
  @page { size: landscape; margin: 0; }
  body { margin: 0; font-family: Georgia, serif; display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f8f9fa; }
  .cert { width: 900px; padding: 60px; border: 3px solid #1e3a5f; background: white; text-align: center; position: relative; }
  .cert::before { content: ''; position: absolute; top: 10px; left: 10px; right: 10px; bottom: 10px; border: 1px solid #1e3a5f; pointer-events: none; }
  .title { font-size: 36px; color: #1e3a5f; margin-bottom: 10px; letter-spacing: 4px; }
  .subtitle { font-size: 16px; color: #666; margin-bottom: 40px; }
  .label { font-size: 14px; color: #888; margin-bottom: 8px; }
  .name { font-size: 32px; color: #1e3a5f; font-weight: bold; margin-bottom: 30px; border-bottom: 2px solid #1e3a5f; display: inline-block; padding-bottom: 5px; }
  .body { font-size: 15px; color: #444; line-height: 1.6; margin-bottom: 40px; max-width: 600px; margin-left: auto; margin-right: auto; }
  .footer { display: flex; justify-content: space-between; margin-top: 40px; font-size: 12px; color: #888; }
</style>
</head>
<body>
<div class="cert">
  <div class="title">CERTIFICATE</div>
  <div class="subtitle">of Membership</div>
  <div class="label">This certifies that</div>
  <div class="name">${fullName}</div>
  <div class="body">
    has been duly approved and certified as a member of the<br/>
    <strong>Association of Certified Accountants (ACA)</strong><br/>
    having met all the requirements for membership.
  </div>
  <div class="footer">
    <span>Membership No: ${certId}</span>
    <span>Date: ${date}</span>
  </div>
</div>
</body>
</html>`;
};

// Coordinate reference (pdfplumber analysis of template PDF, page 595×842, y from bottom):
//   Name "Ameen Afsal Unniyangal":  x=186–394, pdf-lib y=576–596
//   Main date "04 APRIL 2026":      x=234–345, pdf-lib y=347–363  (font: Algerian, size≈16)
//   "Admitted…on: 4th April, 2026": x=220–309, pdf-lib y=108–122  (font: Arial, size≈11)
//   "Membership No:" ends at x=507, "552" at x=511–534, pdf-lib y=126–140


function ordinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1: return "st";
    case 2: return "nd";
    case 3: return "rd";
    default: return "th";
  }
}

export const buildCertificatePdf = async (
  fullName: string,
  certId: string,
  date: string
): Promise<Uint8Array> => {
  const templateRes = await fetch("/certificate-template.pdf");
  if (!templateRes.ok) throw new Error("Certificate template not found");
  const templateBytes = await templateRes.arrayBuffer();

  const pdfDoc = await PDFDocument.load(templateBytes);
  const page = pdfDoc.getPages()[0];
  const pageW = 595;

  const sansFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.TimesRomanBold);

  const cover = (x: number, y: number, w: number, h: number) =>
    page.drawRectangle({ x, y, width: w, height: h, color: rgb(1, 1, 1), borderWidth: 0 });

  // ── Student name ──────────────────────────────────────────────────────────
  cover(90, 570, 415, 33);
  const nameFontSize = 20;
  let nameWidth = boldFont.widthOfTextAtSize(fullName, nameFontSize);
  let actualNameFontSize = nameFontSize;
  if (nameWidth > 410) {
    actualNameFontSize = nameFontSize * (410 / nameWidth);
    nameWidth = boldFont.widthOfTextAtSize(fullName, actualNameFontSize);
  }
  page.drawText(fullName, {
    x: (pageW - nameWidth) / 2,
    y: 578,
    size: actualNameFontSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  });

  // ── Parse date ────────────────────────────────────────────────────────────
  let dateObj: Date;
  try {
    dateObj = parse(date, "MMMM d, yyyy", new Date());
    if (isNaN(dateObj.getTime())) dateObj = new Date();
  } catch {
    dateObj = new Date();
  }
  const day = dateObj.getDate();

  // ── Main date "04 APRIL 2026" ─────────────────────────────────────────────
  const mainDateStr = format(dateObj, "dd MMMM yyyy").toUpperCase();
  const mainDateSize = 16;
  const mainDateWidth = boldFont.widthOfTextAtSize(mainDateStr, mainDateSize);
  const mainDateX = (pageW - mainDateWidth) / 2;
  const rectLeft  = Math.min(229, mainDateX - 3);
  const rectRight = Math.max(351, mainDateX + mainDateWidth + 3);
  cover(rectLeft, 342, rectRight - rectLeft, 24);
  page.drawText(mainDateStr, {
    x: mainDateX,
    y: 348,
    size: mainDateSize,
    font: boldFont,
    color: rgb(0, 0, 0),
  });

  // ── "Admitted to membership on: 4th April, 2026" ─────────────────────────
  const ordStr = `${day}${ordinalSuffix(day)} ${format(dateObj, "MMMM, yyyy")}`;
  const ordWidth = sansFont.widthOfTextAtSize(ordStr, 12);
  // The template's own date runs to x≈316. A short date (e.g. "2nd July, 2026") is
  // narrower than that, so the patch must clear the template's full extent or its
  // trailing characters show through.
  cover(216, 105, Math.max(ordWidth + 10, 104), 22);
  page.drawText(ordStr, {
    x: 220,
    y: 110,
    size: 12,
    font: sansFont,
    color: rgb(0, 0, 0),
  });

  // ── Membership number ─────────────────────────────────────────────────────
  const certIdWidth = sansFont.widthOfTextAtSize(certId, 12);
  cover(507, 124, Math.max(certIdWidth + 10, 40), 22);
  page.drawText(certId, {
    x: 511,
    y: 129,
    size: 12,
    font: sansFont,
    color: rgb(0, 0, 0),
  });

  const saved = await pdfDoc.save();
  return saved.slice();
};

export const downloadCertificate = async (fullName: string, certId: string, date: string): Promise<void> => {
  const pdfBytes = await buildCertificatePdf(fullName, certId, date);
  const blob = new Blob([pdfBytes.buffer as ArrayBuffer], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `ACA certificate (Certified) - ${certId} ${fullName}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const CertificateDownload = ({ fullName, certId, date, applicationId }: CertificateDownloadProps) => {
  const handleClick = async () => {
    await downloadCertificate(fullName, certId, date);
    if (!applicationId) return;
    try {
      const { emailMembershipDocs } = await import("@/lib/membershipDocs");
      await emailMembershipDocs(applicationId, { fullName, certId, date });
      toast({ title: "Documents emailed", description: `Certificate & Letter of Good Standing sent to ${fullName}.` });
    } catch {
      /* download already succeeded; emailing is best-effort */
    }
  };
  return (
    <Button
      variant="outline"
      size="sm"
      title="Download Certificate"
      className="h-8 w-8 p-0 border-border/60 hover:bg-primary hover:text-white hover:border-primary transition-all duration-200"
      onClick={handleClick}
    >
      <Download className="h-3.5 w-3.5" />
    </Button>
  );
};

export default CertificateDownload;
